import { countryOptions } from '@/constants/countryOptions';
import {
    integrations,
    smartPricerEnvironments,
} from '@/pageContents/liftOperatorsContent/config';
import { CreateLiftOperator, LiftOperator } from '@/models/liftoperator';

type FormInputType =
    | 'checkbox'
    | 'textInput'
    | 'markdownInput'
    | 'numberInput'
    | 'moneyInput'
    | 'facilityInput'
    | 'select'
    | 'seoMetaDataInput'
    | 'logoUpload';

export type FormDataConfig = {
    groups: LiftOperatorFormGroup[];
};

export type LiftOperatorFormGroup = {
    label: string;
    fields: LiftOperatorFormField[];

    name?: string;

    onBeforeSubmit?: <T extends LiftOperator | CreateLiftOperator>(
        litOperator: T,
        group: LiftOperatorFormGroup
    ) => T;
};

export type LiftOperatorFormField = {
    name: string;
    label: string;
    component: FormInputType;
    options?: { value: string; label: string }[];
    children?: LiftOperatorFormFieldChild[];

    getValue?: <T = unknown>(
        litOperator: LiftOperator | CreateLiftOperator,
        group: LiftOperatorFormField
    ) => T;

    // custom on change
    onChange?: <T extends LiftOperator | CreateLiftOperator, V = unknown>(
        nextValue: V,
        litOperator: T,
        field: LiftOperatorFormField
    ) => T;

    // cleanup handler before the liftoperator is passed to validation and (if validation passed) submitted
    onBeforeSubmit?: <T extends LiftOperator | CreateLiftOperator>(
        litOperator: T,
        field: LiftOperatorFormField
    ) => T;
};

type LiftOperatorFormFieldChild = LiftOperatorFormField & {
    // this field depends on the value of the parent field
    dependsOnParentValue: unknown;
};

export const liftOperatorFormData: FormDataConfig = {
    groups: [
        {
            label: 'Allgemeine Daten',
            name: 'general',
            fields: [
                {
                    name: 'active',
                    label: 'Aktiv',
                    component: 'checkbox',
                },

                {
                    name: 'disablePortal',
                    label: 'Im Portal deaktivieren',
                    component: 'checkbox',
                },

                {
                    name: 'hideInSupportForm',
                    label: 'Im Kontaktformular ausblenden (z. B. Veranstaltungen, Test-Bergbahnen)',
                    component: 'checkbox',
                },

                {
                    name: 'insuranceEnabled',
                    label: 'Versicherung für Integration aktivieren',
                    component: 'checkbox',
                },

                {
                    name: 'insuranceEnabledForPortal',
                    label: 'Versicherung für Portal aktivieren',
                    component: 'checkbox',
                },

                {
                    name: 'dynamicPricingEnabled',
                    label: 'Dynamic Pricing',
                    component: 'checkbox',
                },
                {
                    name: 'dynamicPricingActive',
                    label: 'SJ Dynamic Pricing aktiv',
                    component: 'checkbox',
                },
                {
                    name: 'disableB2b',
                    label: 'B2B Tool deaktivieren',
                    component: 'checkbox',
                },
                {
                    name: 'disableHospitality',
                    label: 'Hospitality deaktivieren',
                    component: 'checkbox',
                },
                {
                    name: 'voucherEnabled',
                    label: 'Gutscheintool aktivieren',
                    component: 'checkbox',
                },
                {
                    name: 'poolMember',
                    label: 'Pool Alpin Mitglied',
                    component: 'checkbox',
                },
                {
                    name: 'faceValidationEnabled',
                    label: 'Gesichtserkennung aktivieren',
                    component: 'checkbox',
                },
                {
                    name: 'name.de',
                    label: 'Name',
                    component: 'textInput',
                },
                {
                    name: 'alternativeNames',
                    label: 'Alternative Namen',
                    component: 'textInput',
                },
                {
                    name: 'phone',
                    label: 'Telefon',
                    component: 'textInput',
                },
                {
                    name: 'fax',
                    label: 'Fax',
                    component: 'textInput',
                },
                {
                    name: 'homepage',
                    label: 'Homepage',
                    component: 'textInput',
                },
                {
                    name: 'email',
                    label: 'E-Mail',
                    component: 'textInput',
                },
                {
                    name: 'internalEmail',
                    label: 'CC E-Mail Interne Produktion',
                    component: 'textInput',
                },
                {
                    name: 'ticketBccEmail',
                    label: 'Ticket in Kopie an',
                    component: 'textInput',
                },
                {
                    name: 'supportEmail',
                    label: 'Support-E-Mail (Kontaktformular, leer = zentraler Support)',
                    component: 'textInput',
                },
                {
                    name: 'address.street',
                    label: 'Straße',
                    component: 'textInput',
                },
                {
                    name: 'address.zipcode',
                    label: 'PLZ',
                    component: 'textInput',
                },
                {
                    name: 'address.city',
                    label: 'Ort',
                    component: 'textInput',
                },
                {
                    name: 'address.country',
                    label: 'Land',
                    component: 'select',
                    options: countryOptions.map(({ cca2, nameDE }) => ({
                        value: cca2,
                        label: nameDE,
                    })),
                },
                {
                    name: 'infoText.de',
                    label: 'Infotext Deutsch',
                    component: 'markdownInput',
                },
                {
                    name: 'infoText.en',
                    label: 'Infotext Englisch',
                    component: 'markdownInput',
                },

                {
                    name: 'dayTicketsButtonText.de',
                    label: 'Tagestickets Button Text Deutsch',
                    component: 'textInput',
                },
                {
                    name: 'dayTicketsButtonText.en',
                    label: 'Tagestickets Button Text Englisch',
                    component: 'textInput',
                },

                {
                    name: 'seasonTicketsButtonText.de',
                    label: 'Saisonkarten Button Text Deutsch',
                    component: 'textInput',
                },
                {
                    name: 'seasonTicketsButtonText.en',
                    label: 'Saisonkarten Button Text Englisch',
                    component: 'textInput',
                },

                {
                    name: 'slopeKm',
                    label: 'Pisten KM',
                    component: 'numberInput',
                },
                {
                    name: 'slopesBlue',
                    label: 'Anzahl Pisten in blau',
                    component: 'numberInput',
                },
                {
                    name: 'slopesRed',
                    label: 'Anzahl Pisten in rot',
                    component: 'numberInput',
                },
                {
                    name: 'slopesBlack',
                    label: 'Anzahl Pisten in schwarz',
                    component: 'numberInput',
                },

                {
                    name: 'kmRed',
                    label: 'Km rote Piste',
                    component: 'numberInput',
                },

                {
                    name: 'kmBlue',
                    label: 'Km blaue Piste',
                    component: 'numberInput',
                },

                {
                    name: 'kmBlack',
                    label: 'Km schwarze Piste',
                    component: 'numberInput',
                },

                {
                    name: 'routes',
                    label: 'Anzahl Routen',
                    component: 'numberInput',
                },

                {
                    name: 'routesKm',
                    label: 'Anzahl Routen KM',
                    component: 'numberInput',
                },
                {
                    name: 'draglifts',
                    label: 'Anzahl Schlepplifte',
                    component: 'numberInput',
                },
                {
                    name: 'chairlifts',
                    label: 'Anzahl Sessellifte',
                    component: 'numberInput',
                },
                {
                    name: 'gondolas',
                    label: 'Anzahl Gondeln',
                    component: 'numberInput',
                },
                {
                    name: 'aerialTramways',
                    label: 'Anzahl Seilbahnen',
                    component: 'numberInput',
                },
                {
                    name: 'funiculars',
                    label: 'Anzahl Standseilbahnen',
                    component: 'numberInput',
                },
                {
                    name: 'conveyors',
                    label: 'Anzahl Förderbänder',
                    component: 'numberInput',
                },

                {
                    name: 'logos',
                    label: 'Logo hochladen',
                    component: 'logoUpload',
                },
            ],
        },
        {
            label: 'Bergbahnzugang',
            name: 'access',
            fields: [
                {
                    name: 'username',
                    label: 'Username',
                    component: 'textInput',
                },
                {
                    name: 'password',
                    label: 'Password',
                    component: 'textInput',
                },
            ],
        },
        {
            label: 'Facilities',
            name: 'facilities',
            fields: [
                {
                    name: 'facilities',
                    label: '',
                    component: 'facilityInput',
                },
            ],
        },
        {
            label: 'Features',
            name: 'features',
            fields: [
                {
                    name: 'dynamicPricingEnabled',
                    label: 'Dynamic Pricing Aktiviert',
                    component: 'checkbox',
                },
                {
                    name: 'dynamicPricingActive',
                    label: 'SJ Dynamic Pricing Aktiviert',
                    component: 'checkbox',
                },
                {
                    name: 'mobileFlowEnabled',
                    label: 'Mobile Flow Aktiviert',
                    component: 'checkbox',
                },
                {
                    name: 'enableConfiguration',
                    label: 'Verwaltung Standard Shop aktiv',
                    component: 'checkbox',
                },
                {
                    name: 'hideOriginalPrice',
                    label: 'Originalpreis verstecken',
                    component: 'checkbox',
                },
                {
                    name: 'hideBarcode',
                    label: 'Barcode ausblenden',
                    component: 'checkbox',
                },
                {
                    name: 'numbirdsActive',
                    label: 'Numbirds aktiv',
                    component: 'checkbox',
                },
            ],
        },
        {
            label: 'Kontingentierung',
            name: 'contingent',
            fields: [
                {
                    name: 'maxTicketsPerStartDay',
                    label: 'Maximales Kontingent pro Startdatum',
                    component: 'numberInput',
                },
            ],
        },
        {
            label: 'Abrechnung',
            name: 'billing',
            fields: [
                {
                    name: 'liftOperatorPayment',
                    label: 'Eigenes Payment',
                    component: 'checkbox',
                },

                {
                    name: 'signdate',
                    label: 'Vertragspartner seit',
                    component: 'textInput',
                },
                {
                    name: 'fastbillCustomerId',
                    label: 'Fastbill Kundennummer',
                    component: 'textInput',
                },

                {
                    name: 'billingMode',
                    label: 'Abrechnungsmodus',
                    component: 'select',
                    options: [
                        {
                            value: 'CONTRACT_2016',
                            label: 'Vertrag 2016',
                        },
                        {
                            value: 'CONTRACT_2020',
                            label: 'Vertrag 2020',
                        },
                    ],
                },
                {
                    name: 'invoiceProviderName',
                    label: 'alternativer Leistungserbringer',
                    component: 'textInput',
                },
            ],
        },
        {
            label: 'Six Payment',
            name: 'sixCredentials',

            fields: [
                {
                    name: 'sixCredentials.customerId',
                    label: 'Customer ID',
                    component: 'textInput',
                },
                {
                    name: 'sixCredentials.terminalId',
                    label: 'Terminal ID',
                    component: 'textInput',
                },
                {
                    name: 'sixCredentials.username',
                    label: 'Username',
                    component: 'textInput',
                },
                {
                    name: 'sixCredentials.password',
                    label: 'Password',
                    component: 'textInput',
                },
            ],
        },
        {
            label: 'Pfand (bitte Komma mit Punkt eingeben)',
            name: 'deposit',
            fields: [
                {
                    name: 'pickupDepositAmount',
                    label: 'Pickup',
                    component: 'moneyInput',
                },
                {
                    name: 'internalProductionDepositAmount',
                    label: 'Produktion durch BB',
                    component: 'moneyInput',
                },
            ],
        },
        {
            label: 'Versandkosten (bitte Komma mit Punkt eingeben)',
            name: 'shipping',
            fields: [
                {
                    name: 'internalProductionShippingCosts',
                    label: 'Produktion durch BB pro Bestellung',
                    component: 'moneyInput',
                },
            ],
        },
        {
            label: 'Schnittstellen',
            name: 'integration',
            fields: [
                {
                    name: 'integration',
                    label: 'Integration',
                    component: 'select',
                    options: integrations.map(({ type, label }) => ({
                        value: type,
                        label: label,
                    })),

                    children: [
                        {
                            dependsOnParentValue: 'DTA',
                            name: 'dta.username',
                            label: 'DTA Username',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'DTA',
                            name: 'dta.password',
                            label: 'DTA Password',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'DTA',
                            name: 'dta.client',
                            label: 'DTA Client',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'DTA',
                            name: 'dta.pointOfSaleName',
                            label: 'DTA Verkaufsstelle Portal',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'DTA',
                            name: 'dta.integrationPointOfSaleName',
                            label: 'DTA Verkaufsstelle Integration',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'DTA',
                            name: 'dta.integrationClient',
                            label: 'DTA Client Integration',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'DTA',
                            name: 'dta.hospitalityClient',
                            label: 'DTA Client Hospitality',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'BEI',
                            name: 'bei.username',
                            label: 'BEI Username',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'BEI',
                            name: 'bei.password',
                            label: 'BEI Password',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'BEI',
                            name: 'bei.pointOfSaleName',
                            label: 'BEI Point of sale same',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'BEI',
                            name: 'bei.salesChannelShortName',
                            label: 'BEI Sales channel short name',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'AXESS',
                            name: 'axess.soapUrl',
                            label: 'AXESS SOAP URL',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'AXESS',
                            name: 'axess.loginId',
                            label: 'AXESS Login ID',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'AXESS',
                            name: 'axess.username',
                            label: 'AXESS Username',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'AXESS',
                            name: 'axess.password',
                            label: 'AXESS Password',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'AXESS',
                            name: 'axess.soapUsername',
                            label: 'AXESS SOAP Username',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: 'AXESS',
                            name: 'axess.soapPassword',
                            label: 'AXESS SOAP Password',
                            component: 'textInput',
                        },
                    ],
                },
            ],
        },
        {
            label: 'Smart Pricer',
            name: 'smartPricer',
            fields: [
                {
                    name: 'tempSmartPricer',
                    label: 'Smart Pricer aktivieren',
                    component: 'checkbox',
                    onChange: (nextValue, liftOperator, field) => {
                        const { name } = field;
                        if (nextValue === false) {
                            liftOperator[name] = false;
                            liftOperator.smartPricer = null;
                        } else {
                            liftOperator[name] = true;
                            liftOperator.smartPricer = {
                                environment: 'PRODUCTION',
                                customerId: '',
                                apiToken: '',
                                seasonId: '',
                                profile: '',
                            };
                        }

                        return liftOperator;
                    },

                    getValue: <T = boolean>(
                        liftOperator: LiftOperator | CreateLiftOperator
                    ): T => {
                        return !!liftOperator.smartPricer as T;
                    },
                    onBeforeSubmit: (litOperator, field) => {
                        const { name } = field;

                        const value = litOperator[name];

                        // if value of tempSmartPricer is actively set to false
                        // and we have a set smartPricer object then set it to null
                        if (value === false && litOperator.smartPricer) {
                            litOperator.smartPricer = null;
                        }

                        delete litOperator[name];

                        return litOperator;
                    },

                    children: [
                        {
                            dependsOnParentValue: true,
                            name: 'smartPricer.environment',
                            label: 'Environment',
                            component: 'select',
                            options: smartPricerEnvironments.map(
                                ({ type, label }) => ({
                                    value: type,
                                    label: label,
                                })
                            ),
                        },
                        {
                            dependsOnParentValue: true,
                            name: 'smartPricer.customerId',
                            label: 'Customer ID',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: true,
                            name: 'smartPricer.apiToken',
                            label: 'API Token',
                            component: 'textInput',
                        },
                        {
                            dependsOnParentValue: true,
                            name: 'smartPricer.seasonId',
                            label: 'Season ID',
                            component: 'textInput',
                        },
                    ],
                },
            ],
        },
        {
            label: 'INCERT',
            name: 'incert',
            fields: [
                {
                    name: 'incertActive',
                    label: 'Incert aktiv',
                    component: 'checkbox',
                },
                {
                    name: 'incertEndpoint',
                    label: 'Endpoint',
                    component: 'textInput',
                },
                {
                    name: 'incertTerminalId',
                    label: 'Terminal ID',
                    component: 'textInput',
                },
                {
                    name: 'incertPassword',
                    label: 'Passwort',
                    component: 'textInput',
                },
            ],
        },

        {
            label: 'SEO',
            name: 'seo',
            fields: [
                {
                    name: 'metaData',
                    label: '',
                    component: 'seoMetaDataInput',
                },
            ],
        },
    ],
};
